import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { useToast } from '@/hooks/use-toast'
import { Plane } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AirportSearchInput,
  type AirportSearchResult,
} from '@/components/airports/AirportSearchInput'
import { AirportListItem } from '@/components/airports/AirportListItem'
import { haversineDistanceMiles } from '@limo/shared/pricing'

/** Minimum distance in miles between two airports to be considered distinct */
const DUPLICATE_THRESHOLD_MILES = 0.5

/** Default radius for new airports in miles */
const DEFAULT_RADIUS_MILES = 3.0

interface AccountAirportRow {
  readonly id: string
  readonly name: string
  readonly address: string
  readonly lat: number
  readonly lng: number
  readonly radius_miles: number
  readonly is_active: boolean
}

function useActiveAccountId() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const activeAccount = isImpersonating ? impersonatedAccount : account
  return activeAccount?.id ?? null
}

function useAirportsQuery(accountId: string | null) {
  return useQuery({
    queryKey: ['account-airports', accountId],
    queryFn: async () => {
      if (!accountId) return []

      const { data, error } = await supabase
        .from('account_airports')
        .select('*')
        .eq('account_id', accountId)
        .order('name')

      if (error) throw error
      return (data ?? []) as AccountAirportRow[]
    },
    enabled: !!accountId,
  })
}

export function AirportSettingsPage() {
  const accountId = useActiveAccountId()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data: airports, isLoading } = useAirportsQuery(accountId)

  // Add airport mutation
  const addMutation = useMutation({
    mutationFn: async (result: AirportSearchResult) => {
      if (!accountId) throw new Error('No account found')

      // Check for duplicates by lat/lng proximity
      const isDuplicate = (airports ?? []).some(
        (existing) =>
          haversineDistanceMiles(
            existing.lat,
            existing.lng,
            result.lat,
            result.lng
          ) < DUPLICATE_THRESHOLD_MILES
      )

      if (isDuplicate) {
        throw new Error('This airport has already been added')
      }

      const { data, error } = await supabase
        .from('account_airports')
        .insert({
          account_id: accountId,
          name: result.name,
          address: result.address,
          lat: result.lat,
          lng: result.lng,
          radius_miles: DEFAULT_RADIUS_MILES,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-airports', accountId] })
      toast({
        title: 'Airport added',
        description: 'The airport has been added to your list.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Update radius mutation
  const updateRadiusMutation = useMutation({
    mutationFn: async ({ id, radius }: { id: string; radius: number }) => {
      const { error } = await supabase
        .from('account_airports')
        .update({ radius_miles: radius })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-airports', accountId] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Remove airport mutation
  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('account_airports')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-airports', accountId] })
      toast({
        title: 'Airport removed',
        description: 'The airport has been removed from your list.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleAddAirport = useCallback(
    (result: AirportSearchResult) => {
      addMutation.mutate(result)
    },
    [addMutation]
  )

  const handleUpdateRadius = useCallback(
    (id: string, radius: number) => {
      updateRadiusMutation.mutate({ id, radius })
    },
    [updateRadiusMutation]
  )

  const handleRemoveAirport = useCallback(
    (id: string) => {
      removeMutation.mutate(id)
    },
    [removeMutation]
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  const airportList = airports ?? []

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Airports</CardTitle>
          <CardDescription>
            Configure the airports your company services. Customer addresses near these
            airports will be automatically classified as airport transfers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search input */}
          <div>
            <AirportSearchInput
              onSelect={handleAddAirport}
              disabled={addMutation.isPending}
            />
          </div>

          {/* Airport list */}
          {airportList.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Your Airports ({airportList.length})
              </h3>
              {airportList.map((airport) => (
                <AirportListItem
                  key={airport.id}
                  airport={airport}
                  onUpdateRadius={handleUpdateRadius}
                  onRemove={handleRemoveAirport}
                  isRemoving={removeMutation.isPending}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Plane className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-medium text-muted-foreground">No airports configured</p>
              <p className="text-sm text-muted-foreground mt-1">
                Search for airports above to enable automatic airport transfer detection.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
